// frontend/src/services/usuariosService.js
// Servicio para gestión de usuarios (solo accesible para Admin)
// Usa la URL /api/auth/usuarios/ que está fuera del prefijo /api/taller/
import axios from 'axios';
import { API_ROOT } from './apiClient';

// Cliente que apunta a la raíz de la API para el módulo de autenticación/usuarios
const authApiClient = axios.create({
  baseURL: `${API_ROOT}/auth/`,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
});

// Agregar token JWT en cada request
authApiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const BASE_URL = 'usuarios';

const usuariosService = {
  /**
   * Listar todos los usuarios del sistema.
   * Requiere rol Admin.
   */
  listar: async () => {
    const response = await authApiClient.get(`${BASE_URL}/`);
    return response.data;
  },

  listarPorRol: async (rol) => {
    const response = await authApiClient.get(`${BASE_URL}/?rol=${rol}&page_size=500`);
    return response.data;
  },

  /**
   * Crear un nuevo usuario.
   * @param {Object} datos - { username, email, first_name, last_name, password, password_confirm, rol }
   */
  crear: async (datos) => {
    const response = await authApiClient.post(`${BASE_URL}/`, datos);
    return response.data;
  },

  /**
   * Obtener datos de un usuario por ID.
   */
  obtener: async (id) => {
    const response = await authApiClient.get(`${BASE_URL}/${id}/`);
    return response.data;
  },

  /**
   * Editar datos de un usuario (nombre, email, perfil).
   */
  editar: async (id, datos) => {
    const response = await authApiClient.patch(`${BASE_URL}/${id}/`, datos);
    return response.data;
  },

  /**
   * Eliminar usuario permanentemente.
   */
  eliminar: async (id) => {
    const response = await authApiClient.delete(`${BASE_URL}/${id}/`);
    return response.data;
  },

  /**
   * Activar o desactivar un usuario.
   * @returns {Object} { is_active, user, message }
   */
  toggleActivo: async (id) => {
    const response = await authApiClient.post(`${BASE_URL}/${id}/toggle_activo/`);
    return response.data;
  },

  /**
   * Cambiar el rol de un usuario.
   * @param {string} rol - Uno de: 'admin', 'gerente', 'tecnico', 'usuario'
   */
  cambiarRol: async (id, rol) => {
    const response = await authApiClient.post(`${BASE_URL}/${id}/cambiar_rol/`, { rol });
    return response.data;
  },

  resyncPermisos: async (id) => {
    const response = await authApiClient.post(`${BASE_URL}/${id}/resync_permisos/`);
    return response.data;
  },

  /**
   * Clonar un usuario copiando perfil y permisos.
   */
  clonar: async (id, datos) => {
    const response = await authApiClient.post(`${BASE_URL}/${id}/clonar/`, datos);
    return response.data;
  },

  /**
   * Cambiar la contraseña de cualquier usuario (solo Admin).
   */
  setPassword: async (id, nuevaPassword) => {
    const response = await authApiClient.post(`${BASE_URL}/${id}/set_password/`, { new_password: nuevaPassword });
    return response.data;
  },

  /**
   * Obtener matriz de permisos CRUD por modelo para un usuario.
   */
  obtenerPermisos: async (id) => {
    const response = await authApiClient.get(`${BASE_URL}/${id}/permisos/`);
    return response.data;
  },

  /**
   * Guardar matriz de permisos CRUD por modelo para un usuario.
   * @param {number} id
   * @param {Array} permisos
   * @param {Object} alcance
   */
  guardarPermisos: async (id, permisos, alcance = null) => {
    const payload = { permisos };
    if (alcance && typeof alcance === 'object') {
      payload.alcance = alcance;
    }
    const response = await authApiClient.put(`${BASE_URL}/${id}/permisos/`, payload);
    return response.data;
  },
};

export default usuariosService;
